import { forwardRef, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { groupBy } from 'lodash';
import { tv } from 'tailwind-variants';

import { LoadError } from '@/components/ui/load-error';
import { Loading } from '@/components/ui/old/Loading';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useConfetti } from '@/providers/confetti';
import { CreateAlertTask } from '@/task-manager/api/mutations/create-alert-task';
import { CreateStandardTask } from '@/task-manager/api/mutations/create-standard-task';
import { UpdateTask } from '@/task-manager/api/mutations/update-task';
import { ListTasks } from '@/task-manager/api/queries/list-tasks';
import { AlertSuggestionsColumn } from '@/task-manager/components/alert-suggestions-column';
import { AlertFeedbackDialog } from '@/task-manager/components/task-details/actions/alert-feedback-dialog';
import { RequestResetDialog } from '@/task-manager/components/task-details/actions/request-reset-dialog';
import { TasksColumn } from '@/task-manager/components/tasks-column';
import { TaskCardType } from '@/task-manager/entities/task-card-type';
import type { TaskPreview } from '@/task-manager/entities/task-preview';
import { TaskStatus } from '@/task-manager/entities/task-status';
import { useFilterConfig } from '@/task-manager/hooks/use-filter-config';

export type TasksBoardProps = {
  filters: ListTasks.Filters;
  className?: string;
  onTaskOpen: (taskId: string) => void;
  asset: { id: string; frontendId: string };
};

const TasksBoard = forwardRef<HTMLDivElement, TasksBoardProps>(function (
  { asset, filters, onTaskOpen, className },
  ref,
) {
  const history = useHistory();
  const featureFlag = useFeatureFlag();
  const confetti = useConfetti();
  const updateTask = UpdateTask.useMutation();
  const createStandardTask = CreateStandardTask.useMutation();
  const createAlertTask = CreateAlertTask.useMutation();
  const listTasks = ListTasks.useQuery(
    {
      filters: {
        ...filters,
        topId: asset.frontendId,
      },
    },
    {
      refetchInterval: 30_000,
      keepPreviousData: true,
    },
  );
  const [feedbackTask, setFeedbackTask] = useState<TaskPreview | undefined>(
    undefined,
  );
  const [resetScoreTask, setResetScoreTask] = useState<TaskPreview | undefined>(
    undefined,
  );
  const { shouldHideCardType } = useFilterConfig(filters);

  const filteredSuggestions = useMemo(() => {
    if (shouldHideCardType(TaskCardType.ALERT)) {
      return [];
    }

    return (
      listTasks.data?.alertSuggestions.filter((suggestion) =>
        suggestion.frontendId.includes(asset.frontendId),
      ) ?? []
    );
  }, [listTasks.data?.alertSuggestions, asset.frontendId, shouldHideCardType]);

  const tasksPerStatus = useMemo(
    () =>
      groupBy(listTasks.data?.tasks, (task) => task.status) as Record<
        TaskStatus,
        TaskPreview[] | undefined
      >,
    [listTasks.data],
  );

  const style = tasksBoardStyle();

  if (listTasks.isLoading) {
    return <Loading />;
  }

  if (listTasks.isError) {
    return <LoadError title="An error occurred while loading the task board" />;
  }

  return (
    <div
      ref={ref}
      data-testid="tasks-board"
      className={style.container({ className })}
    >
      <div className={style.alertsContainer()}>
        <AlertSuggestionsColumn
          alertSuggestions={filteredSuggestions}
          draggable
          onAlertTaskCreate={async function (input) {
            const task = await createAlertTask.mutateAsync(input);
            history.replace({
              search: new URLSearchParams({ id: task.id }).toString(),
            });
          }}
        />
      </div>
      <div className={style.tasksContainer()}>
        {[
          TaskStatus.OPEN,
          TaskStatus.IN_PROGRESS,
          TaskStatus.EXTERNAL_REVIEW,
          TaskStatus.DONE,
        ].map((status) => (
          <TasksColumn
            key={status}
            draggable
            status={status}
            tasks={tasksPerStatus[status] ?? []}
            onTaskOpen={onTaskOpen}
            onTaskUpdate={async function (
              input: UpdateTask.Input,
              task: TaskPreview,
            ) {
              try {
                if (input.status) {
                  // Call setFeedbackTask even before the end of updateTask.mutateAsync, if it fails we call setResetScoreTask with undefined
                  setFeedbackTask({ ...task, status: input.status });
                }
                await updateTask.mutateAsync(input);
              } catch (_) {
                setFeedbackTask(undefined);
              }
            }}
            onStandardTaskCreate={
              status !== TaskStatus.DONE
                ? async function (input) {
                    const task = await createStandardTask.mutateAsync({
                      ...input,
                      assetId: asset.id,
                    });
                    history.replace({
                      search: new URLSearchParams({ id: task.id }).toString(),
                    });
                  }
                : undefined
            }
            onAlertTaskCreate={async function (input) {
              const task = await createAlertTask.mutateAsync(input);
              history.replace({
                search: new URLSearchParams({ id: task.id }).toString(),
              });
            }}
          />
        ))}
      </div>
      {feedbackTask ? (
        <AlertFeedbackDialog
          task={feedbackTask}
          newStatus={feedbackTask.status}
          open={Boolean(feedbackTask)}
          onClose={function () {
            setResetScoreTask(feedbackTask);
            setFeedbackTask(undefined);
          }}
        />
      ) : null}
      {resetScoreTask ? (
        <RequestResetDialog
          cancelText="Skip for now"
          task={resetScoreTask}
          open={Boolean(resetScoreTask)}
          onClose={function () {
            setResetScoreTask(undefined);
            if (featureFlag('taskmanagerConfetti')) {
              confetti({});
            }
          }}
        />
      ) : null}
    </div>
  );
});

const tasksBoardStyle = tv({
  slots: {
    container: `
      flex
      size-full
      flex-row
      overflow-y-hidden
    `,
    alertsContainer: `
      w-96
      flex-shrink-0
      border-r
      px-4
      pt-4
    `,
    tasksContainer: `
      grid
      flex-grow
      grid-cols-[repeat(4,_minmax(theme(spacing.72),_theme(spacing.80)))]
      gap-8
      px-4
      pt-4
    `,
  },
});

TasksBoard.displayName = 'TasksBoard';
export { TasksBoard };
